// QualitySummary — the resting right-panel quality verdict, pure.
//
// A GLANCEABLE verdict, not an explorable tree. The owner found the earlier
// nested drill-down (axes → constraints → evidence, inline) cluttered ("grötigt")
// — a right panel should read at a glance, "hellre lite för lite info, och en
// länk till 'Visa mer'". So the resting view is now flat:
//
//   - the report-card SCORE at the top,
//   - the THREE quality axes (factual / linguistic / pedagogical) as ONE LINE
//     each: a plain verdict (Meets / Advisory / Blocks export / Not yet assessed)
//     plus at most the single worst issue — no accordions, no in-panel drill-down,
//   - a single "Show details" door (a callback the shell wires to open the full
//     per-constraint report — `FullQualityView` — as its own tab).
//
// The linguistic axis reads a CONCRETE reading level instead of an absolute,
// audience-blind word: "~B2" (CEFR, estimated from the LIX score — see `cefr.ts`)
// answers "hard for whom?" far better than "hard"/"svår". The audience-RELATIVE
// reading ("~B1, your audience is B2, so this is easier than target") needs the
// target level in the panel's data; `PolicyEvaluation` does not carry it today, so
// the panel shows the concrete band alone — plumbing the target level is a
// flagged follow-up (the pure comparison, `compareCefr`, is ready in `cefr.ts`).
//
// The verdict per axis is derived from the SAME pure folds the detail view uses
// (`axisVerdicts` over the `policy` prop), so glance and detail cannot disagree.
// Colour is never the sole channel (WCAG SC 1.4.1): every verdict badge pairs its
// tone with an icon AND a word. Without a policy the panel falls back to the
// material-level verdicts (grounding + readability) as the same one-line shape.
//
// The quality data arrives as a PROP; the component holds no state and does no
// I/O. Its types are the source of truth for the shape (ports.ts imports them).

import type * as Core from "@bilda/core";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import type { Claim } from "../ClaimRow/ClaimRow.js";
import { HelpHint } from "../HelpHint/HelpHint.js";
import {
    type AxisVerdictLine,
    axisHeadlineBite,
    axisVerdicts,
    constraintKind,
    outcomeBadge,
    pct,
    usePolicyLabels,
} from "../PolicyReport/PolicyReport.js";
import { Text } from "../Text/Text.js";
import { type CefrBand, lixToCefr } from "./cefr.js";
import "./QualitySummary.css";

// The five LIX readability bands (mirrors `D.Quality.lix.LixCategory`). Parsed
// at the wire boundary; `undefined` when the content had no words to score.
export type ReadabilityBand =
    | "very_easy"
    | "easy"
    | "medium"
    | "hard"
    | "very_hard";

// A material statement (its node) and the knowledge-bank claims it rests on.
export type StatementBacking = {
    readonly node: string;
    readonly text: string;
    readonly claims: readonly Claim[];
};

// One backed statement with one source passage behind it (via a claim): the
// margin anchor, a source's "Used in" row. `page` is absent when the source
// has no pages.
export type StatementSource = {
    readonly statementId: string;
    readonly statement: string;
    readonly quote: string;
    readonly source: { readonly id: string; readonly title: string };
    readonly page: number | undefined;
};

// A material's statement sources, in reading order: every located passage of
// every claim behind each backed statement (empty while unread or
// ungrounded).
export const statementSourcesOf = (
    quality: MaterialQuality | null,
): readonly StatementSource[] =>
    quality === null || quality.grounding.type === "no-sources"
        ? []
        : quality.grounding.backing.flatMap((b) =>
              b.claims.flatMap((c) =>
                  c.backing
                      .filter((p) => p.located)
                      .map(
                          (p): StatementSource => ({
                              statementId: b.node,
                              statement: b.text,
                              quote: p.exact,
                              source: { id: p.sourceId, title: p.sourceTitle },
                              page: p.page,
                          }),
                      ),
              ),
          );

// The factual axis. Either the material has a source (it derives from one,
// or a claim backing one of its statements cites one) or none yet — an
// advisory, NOT a failure.
export type Grounding =
    | {
          readonly type: "grounded";
          // Per kind, the items a knowledge-bank claim backs (`cited`) and the
          // unsupported ones (`uncited`); computed by the server, never stored.
          readonly support: Core.Quality.CitationCoverage.CitationCoverage;
          // Each statement a live claim backs, with those claims.
          readonly backing: readonly StatementBacking[];
          readonly sourceTitles: readonly string[];
      }
    | { readonly type: "no-sources" };

// One leaf section's LIX (a torus page, a freeform chapter) — lets a "heavy
// text" finding name where.
export type SectionLix = {
    readonly id: string;
    readonly title: string;
    readonly lix: number;
    readonly band: ReadabilityBand | undefined;
};

// The linguistic axis: LIX score + its band, with the counts the score derives
// from, plus the per-section reads. `band` is absent for empty content.
export type Readability = {
    readonly lix: number;
    readonly band: ReadabilityBand | undefined;
    readonly words: number;
    readonly sentences: number;
    readonly sections: readonly SectionLix[];
};

// The two axes side by side — never averaged.
export type MaterialQuality = {
    readonly grounding: Grounding;
    readonly readability: Readability;
};

// === Verdicts — the ONE derivation every surface shares ===
//
// Each axis gets a verdict from ONE pure fold over MaterialQuality, so the
// panels' warning tones cannot disagree. "not_computed" is an axis with nothing
// to measure (no linked sources, no words) — an advisory state to explain,
// never a problem to alarm on.

export type AxisVerdict = "clean" | "attention" | "not_computed";

export type QualityVerdicts = {
    readonly coverage: AxisVerdict;
    readonly readability: AxisVerdict;
};

// Items still lacking backing, across every kind.
const uncitedCount = (
    coverage: Core.Quality.CitationCoverage.CitationCoverage,
): number => coverage.reduce((n, k) => n + k.uncited.length, 0);

export const qualityVerdicts = (q: MaterialQuality): QualityVerdicts => ({
    coverage:
        q.grounding.type === "no-sources" || q.grounding.support.length === 0
            ? "not_computed"
            : uncitedCount(q.grounding.support) > 0
              ? "attention"
              : "clean",
    readability: readabilityVerdict(q.readability.band),
});

// The readability verdict alone — shared with the library row chip (C4) so the
// row and this panel flag the same bands: hard or very hard text needs
// attention; no words → nothing measured.
export const readabilityVerdict = (
    band: ReadabilityBand | undefined,
): AxisVerdict =>
    band === undefined
        ? "not_computed"
        : band === "hard" || band === "very_hard"
          ? "attention"
          : "clean";

export type QualitySummaryProps = {
    readonly quality: MaterialQuality;
    // Names the region for AT (caller-owned / localised).
    readonly ariaLabel: string;
    // The material's policy evaluation (`evaluateMaterialPolicy` port). When
    // present, the three policy axes are the verdict; absent, the panel falls
    // back to the material-level verdicts (grounding + readability).
    readonly policy?: Core.Policy.Evaluate.PolicyEvaluation;
    // The "Show details" door. When provided, a "Show details" button renders and
    // calls this on activation — the shell opens the full per-constraint report
    // (`FullQualityView`) as its own tab. Progressive disclosure as a DOOR, not
    // in-panel folding. Absent → no button (nothing to open yet).
    readonly onOpenFullQuality?: () => void;
    readonly className?: string;
};

// === Material verdict → the axis lines the no-policy fallback shows ===
//
// Without a policy the panel still has the material-level reads. Each material
// axis verdict maps onto the SAME `Bite` visual language the policy axes use, so
// the two modes render identically: clean → a met check, attention → a quiet
// advisory, not_computed → an honest "not yet".
const verdictToBite = (v: AxisVerdict): Core.Policy.Evaluate.Bite => {
    switch (v) {
        case "clean":
            return "pass";
        case "attention":
            return "warn";
        case "not_computed":
            return "pending";
    }
};

export const QualitySummary = ({
    quality,
    ariaLabel,
    policy,
    onOpenFullQuality,
    className,
}: QualitySummaryProps): ReactNode => {
    const { t } = useLingui();
    const { axisLabels, kindLabels, axisWord } = usePolicyLabels();
    const { readability } = quality;

    // The concrete, estimated reading level for the linguistic axis: "~B2" plus a
    // HelpHint that it is an estimate. `undefined` band (no words) → not scored.
    const readabilityRead = (): ReactNode => {
        if (readability.band === undefined) {
            return (
                <Text as="span" size="sm" tone="dim">
                    {t`Not scored`}
                </Text>
            );
        }
        const cefr: CefrBand = lixToCefr(readability.lix);
        return (
            <span className="bilda-quality-summary__cefr">
                <Text as="span" size="sm" tone="dim">
                    {t`~${cefr}`}
                </Text>
                <HelpHint label={t`Explanation: reading level`}>
                    <Trans>
                        An estimate of the reading level (CEFR, A1–C2) from the
                        text's LIX score. LIX and CEFR measure different things,
                        so treat this as approximate.
                    </Trans>
                </HelpHint>
            </span>
        );
    };

    // One axis line: label at the start; the verdict badge (and, for the
    // linguistic axis, the CEFR reading) at the end; at most one worst-issue line
    // under it. `extra` is the linguistic axis's concrete reading.
    const axisLine = (
        key: string,
        label: string,
        bite: Core.Policy.Evaluate.Bite | null,
        worst: string | null,
        extra: ReactNode,
    ): ReactNode => (
        <div key={key} className="bilda-quality-summary__axis-line">
            <div className="bilda-quality-summary__axis-head">
                <Text as="span" size="sm" weight="medium">
                    {label}
                </Text>
                <span className="bilda-quality-summary__row-end">
                    {extra}
                    {bite !== null ? (
                        outcomeBadge(bite, axisWord(bite))
                    ) : (
                        <Text as="span" size="sm" tone="dim">
                            {t`No checks in this policy`}
                        </Text>
                    )}
                </span>
            </div>
            {worst !== null ? (
                <Text size="sm" tone="dim">
                    {worst}
                </Text>
            ) : null}
        </div>
    );

    // A policy axis → its one-line verdict. The worst-issue line names the most
    // severe measured constraint's kind, but only when the axis actually warns or
    // blocks (a clean or pending axis has no issue to surface).
    const policyAxisLine = (line: AxisVerdictLine): ReactNode => {
        const bite = axisHeadlineBite(line.roll);
        const isLinguistic = line.axis === "linguistic";
        const worst =
            line.worst !== null &&
            (line.worst.bite === "warn" || line.worst.bite === "block")
                ? kindLabels[constraintKind(line.worst.constraintId)]
                : null;
        return axisLine(
            line.axis,
            axisLabels[line.axis],
            bite,
            worst,
            isLinguistic ? readabilityRead() : null,
        );
    };

    // The register line — the whole point of the write view: it informs, it does
    // not gate. Derived from the evaluation's own enforcement so the copy can
    // never drift from what the boundary actually does.
    const registerLine = (
        evaluation: Core.Policy.Evaluate.PolicyEvaluation,
    ): ReactNode => (
        <Text size="sm" tone="dim">
            {evaluation.enforcement === "advisory"
                ? t`Advisories only — nothing here blocks your work.`
                : t`The export gate is on — anything that blocks export must be resolved first.`}
        </Text>
    );

    // The score head, shared by both modes: a calm 0–100 or an honest "not yet".
    const scoreHead = (overall: number | null): ReactNode => (
        <div className="bilda-quality-summary__head">
            <div className="bilda-quality-summary__score">
                {overall !== null ? (
                    <>
                        <Text
                            as="span"
                            size="2xl"
                            weight="semibold"
                            className="bilda-quality-summary__score-value"
                        >
                            {overall}
                        </Text>
                        <Text as="span" size="sm" tone="dim">
                            {t`/100`}
                        </Text>
                    </>
                ) : (
                    <Text size="sm" tone="dim">
                        {t`Not yet measured`}
                    </Text>
                )}
            </div>
            <div className="bilda-quality-summary__score-label">
                <Text as="div" size="sm" weight="medium" tone="dim">
                    {t`Quality score`}
                </Text>
                <HelpHint label={t`Explanation: quality score`}>
                    <Trans>
                        The average of the checks that could be measured — a
                        reading of the material's quality, separate from what
                        blocks an export.
                    </Trans>
                </HelpHint>
            </div>
        </div>
    );

    // The verdict body: the three policy axes when a policy is present, else the
    // material-level fallback (facts + language) in the same one-line shape.
    const body = (): ReactNode => {
        if (policy !== undefined) {
            const overall = pct(policy.score);
            return (
                <>
                    {scoreHead(overall)}
                    {registerLine(policy)}
                    <div className="bilda-quality-summary__axes">
                        {axisVerdicts(policy).map(policyAxisLine)}
                    </div>
                </>
            );
        }
        const v = qualityVerdicts(quality);
        return (
            <div className="bilda-quality-summary__axes">
                {axisLine(
                    "factual",
                    axisLabels.factual,
                    verdictToBite(v.coverage),
                    null,
                    null,
                )}
                {axisLine(
                    "linguistic",
                    axisLabels.linguistic,
                    verdictToBite(v.readability),
                    null,
                    readabilityRead(),
                )}
            </div>
        );
    };

    return (
        <section
            aria-label={ariaLabel}
            className={`bilda-quality-summary${className ? ` ${className}` : ""}`}
        >
            {body()}
            {onOpenFullQuality !== undefined ? (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onOpenFullQuality}
                    className="bilda-quality-summary__details"
                >
                    {t`Show details`}
                </Button>
            ) : null}
        </section>
    );
};
